import React, { useEffect, useRef, useState } from "react";

import {
  Heart,
  Share2,
  Bookmark,
  MoreVertical,
  Play,
  Volume2,
  VolumeX,
  Edit3,
  Trash2,
  Disc,
  Eye,
} from "lucide-react";

import "./PopThumbnailCard.css";

const formatMetric = (value = 0) => {
  const number = Number(value) || 0;

  if (number >= 1000000) {
    return `${(number / 1000000).toFixed(1).replace(".0", "")}M`;
  }

  if (number >= 1000) {
    return `${(number / 1000).toFixed(1).replace(".0", "")}K`;
  }

  return String(number);
};

const PopThumbnailCard = ({
  pop,
  mode = "grid",
  active = false,
  muted = true,
  onMuteChange,
  onClick,
  onView,
  onEdit,
  onDelete,
  onShare,
  onSave,
  onLike,
  isSaved: controlledIsSaved,
  saving = false,
  customMenuOptions = [],
  showActions = true,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const [playingStarted, setPlayingStarted] = useState(false);
  const [playerError, setPlayerError] = useState(false);

  const [isLiked, setIsLiked] = useState(Boolean(pop?.isLiked));
  const [likeCount, setLikeCount] = useState(Number(pop?.likes) || 0);

  const [localIsSaved, setLocalIsSaved] = useState(Boolean(pop?.isSaved));

  const [showHeartSplash, setShowHeartSplash] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [playCount, setPlayCount] = useState(0);

  const menuRef = useRef(null);
  const videoRef = useRef(null);
  const lastTapRef = useRef(0);
  const playCountRef = useRef(0);
  const heartTimerRef = useRef(null);
  const viewTrackedRef = useRef(false);

  const isPlayerMode = mode === "player";
  const isGridMode = !isPlayerMode;

  const videoUrl = pop?.videoUrl || "";
  const thumbnailUrl = pop?.thumbnailUrl || "";

  const isSaved =
    controlledIsSaved !== undefined ? Boolean(controlledIsSaved) : localIsSaved;

  const isPlaying =
    isPlayerMode &&
    active &&
    playerReady &&
    !userPaused &&
    !playerError &&
    playCount < 3 &&
    Boolean(videoUrl);

  useEffect(() => {
    setIsLiked(Boolean(pop?.isLiked));
    setLikeCount(Number(pop?.likes) || 0);
    setLocalIsSaved(Boolean(pop?.isSaved));
  }, [pop?.popId, pop?.popTag, pop?.isLiked, pop?.likes, pop?.isSaved]);

  useEffect(() => {
    setPlayerReady(false);
    setPlayingStarted(false);
    setPlayerError(false);
    setUserPaused(false);
    setPlayCount(0);

    playCountRef.current = 0;
  }, [videoUrl]);

  useEffect(() => {
    if (!isPlayerMode || !active) {
      setUserPaused(false);
      setPlayingStarted(false);
      setShowMenu(false);
      setIsExpanded(false);
    }
  }, [isPlayerMode, active]);

  useEffect(() => {
    const video = videoRef.current;

    if (!video) return;

    if (!isPlaying) {
      video.pause();
      return;
    }

    const promise = video.play();

    promise?.catch((error) => {
      if (error?.name === "AbortError" || error?.name === "NotAllowedError") {
        return;
      }

      console.error("Pop playback failed:", error);
    });
  }, [isPlaying]);

  useEffect(() => {
    if (!showMenu) return;

    const handleOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener("pointerdown", handleOutside);

    return () => {
      document.removeEventListener("pointerdown", handleOutside);
    };
  }, [showMenu]);

  useEffect(() => {
    return () => {
      if (heartTimerRef.current) {
        clearTimeout(heartTimerRef.current);
      }
    };
  }, []);

  if (!pop) return null;

  const handleOpen = (event) => {
    event?.stopPropagation();
    onClick?.(pop);
  };

  const handleAction = (event, action) => {
    event?.stopPropagation();
    setShowMenu(false);
    action?.(pop);
  };

  const handleLike = (event) => {
    event?.stopPropagation();

    const next = !isLiked;

    setIsLiked(next);

    setLikeCount((previous) =>
      next ? previous + 1 : Math.max(0, previous - 1),
    );

    onLike?.(pop, next);
  };

  const handleSave = async (event) => {
    event?.stopPropagation();

    if (saving || !onSave) return;

    setShowMenu(false);

    const next = !isSaved;

    if (controlledIsSaved === undefined) {
      setLocalIsSaved(next);
    }

    try {
      const result = await onSave(pop, next);

      if (result === false && controlledIsSaved === undefined) {
        setLocalIsSaved(!next);
      }
    } catch (error) {
      if (controlledIsSaved === undefined) {
        setLocalIsSaved(!next);
      }

      console.error("Unable to save Pop:", error);
    }
  };

  const triggerDoubleTapLike = () => {
    setShowHeartSplash(true);

    if (heartTimerRef.current) {
      clearTimeout(heartTimerRef.current);
    }

    heartTimerRef.current = setTimeout(() => {
      setShowHeartSplash(false);
    }, 1000);

    if (!isLiked) {
      setIsLiked(true);
      setLikeCount((previous) => previous + 1);
      onLike?.(pop, true);
    }
  };

  const handlePlayerClick = (event) => {
    event.stopPropagation();

    if (!isPlayerMode || !active) {
      onClick?.(pop);
      return;
    }

    const now = Date.now();

    if (now - lastTapRef.current < 300) {
      triggerDoubleTapLike();
      lastTapRef.current = 0;
      return;
    }

    lastTapRef.current = now;

    if (!videoUrl || playerError || playCount >= 3) return;

    setUserPaused((previous) => !previous);
  };

  const handleVideoEnded = () => {
    const video = videoRef.current;

    if (!video) return;

    const nextCount = playCountRef.current + 1;

    playCountRef.current = nextCount;
    setPlayCount(nextCount);

    if (nextCount >= 3) {
      setUserPaused(true);
      return;
    }

    video.currentTime = 0;
    video.play().catch(() => {});
  };

  const handleTimeUpdate = (event) => {
    const video = event.currentTarget;

    if (!active || viewTrackedRef.current || video.currentTime < 3) {
      return;
    }

    const duration =
      Number.isFinite(video.duration) && video.duration > 0
        ? video.duration
        : 0;

    const completionPercent = duration
      ? Math.min(100, (video.currentTime / duration) * 100)
      : 0;

    viewTrackedRef.current = true;

    onView?.(pop, {
      watchSeconds: Number(video.currentTime.toFixed(2)),
      completionPercent: Number(completionPercent.toFixed(2)),
      referrerType: "feed",
    });
  };

  const handlePlayerError = (event) => {
    console.error(
      `Pop playback failed: ${pop.popTag || pop.popId}`,
      event.currentTarget?.error,
    );

    setPlayerReady(false);
    setPlayingStarted(false);
    setPlayerError(true);
  };

  const menuOptions = Array.isArray(customMenuOptions) ? customMenuOptions : [];

  const hasMenu =
    Boolean(onEdit) ||
    Boolean(onDelete) ||
    Boolean(onShare) ||
    (isGridMode && Boolean(onSave)) ||
    menuOptions.length > 0;

  const renderMenu = () => {
    if (!hasMenu) return null;

    return (
      <div ref={menuRef} className="pop-thumbnail-card__actions">
        <button
          type="button"
          className="pop-thumbnail-card__action-btn"
          onClick={(event) => {
            event.stopPropagation();
            setShowMenu((previous) => !previous);
          }}
          aria-label="More options"
          aria-expanded={showMenu}
          aria-haspopup="menu"
        >
          <MoreVertical size={22} />
        </button>

        {showMenu && (
          <div className="pop-thumbnail-card__dropdown" role="menu">
            {onEdit && (
              <button
                type="button"
                role="menuitem"
                className="pop-thumbnail-card__dropdown-item"
                onClick={(event) => handleAction(event, onEdit)}
              >
                <span>Edit Pop</span>
              </button>
            )}

            {onShare && (
              <button
                type="button"
                role="menuitem"
                className="pop-thumbnail-card__dropdown-item"
                onClick={(event) => handleAction(event, onShare)}
              >
                <span>Share Pop</span>
              </button>
            )}

            {isGridMode && onSave && (
              <button
                type="button"
                role="menuitem"
                className="pop-thumbnail-card__dropdown-item"
                onClick={handleSave}
                disabled={saving}
              >
                <Bookmark size={16} fill={isSaved ? "currentColor" : "none"} />
                <span>
                  {saving ? "Saving..." : isSaved ? "Unsave Pop" : "Save Pop"}
                </span>
              </button>
            )}

            {menuOptions.map((option, index) => (
              <button
                key={`${option.label || "option"}-${index}`}
                type="button"
                role="menuitem"
                className={`pop-thumbnail-card__dropdown-item ${
                  option.className || ""
                }`}
                disabled={Boolean(option.disabled)}
                onClick={(event) => {
                  if (option.disabled) return;
                  handleAction(event, option.onClick);
                }}
              >
                {option.icon}
                <span>{option.label}</span>
              </button>
            ))}

            {onDelete && (
              <button
                type="button"
                role="menuitem"
                className="pop-thumbnail-card__dropdown-item pop-thumbnail-card__dropdown-item--delete"
                onClick={(event) => handleAction(event, onDelete)}
              >
                <span>Delete Pop</span>
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <article
      className={[
        "pop-thumbnail-card",
        isGridMode ? "pop-thumbnail-card--grid" : "pop-thumbnail-card--player",
        active ? "pop-thumbnail-card--active" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="pop-thumbnail-card__media">
        {isGridMode && (
          <div className="pop-thumbnail-card__grid">
            <button
              type="button"
              className="pop-thumbnail-card__grid-media"
              onClick={handleOpen}
              aria-label={`Open ${pop.title || "Pop"}`}
            >
              {thumbnailUrl ? (
                <img
                  className="pop-thumbnail-card__poster"
                  src={thumbnailUrl}
                  alt={pop.title || "Pop"}
                  loading="lazy"
                  draggable={false}
                />
              ) : videoUrl ? (
                <video
                  className="pop-thumbnail-card__video pop-thumbnail-card__video--preview"
                  src={videoUrl}
                  muted
                  playsInline
                  preload="metadata"
                  controls={false}
                  disablePictureInPicture
                  disableRemotePlayback
                  controlsList="nodownload"
                  onContextMenu={(event) => event.preventDefault()}
                />
              ) : (
                <div className="pop-thumbnail-card__empty">
                  <Play size={32} />
                  <span>No media</span>
                </div>
              )}

              <div className="pop-thumbnail-card__gradient pop-thumbnail-card__gradient--bottom" />

              <span className="pop-thumbnail-card__grid-play">
                <Play size={22} fill="currentColor" />
              </span>

              <span className="pop-thumbnail-card__grid-views">
                <Eye size={14} />
                {formatMetric(pop.views)}
              </span>
            </button>

            {hasMenu && (
              <div className="pop-thumbnail-card__grid-menu">
                {renderMenu()}
              </div>
            )}
          </div>
        )}

        {isPlayerMode && (
          <>
            {videoUrl && !playerError ? (
              <>
                <div
                  className="pop-thumbnail-card__player"
                  onClick={handlePlayerClick}
                >
                  <video
                    ref={videoRef}
                    className="pop-thumbnail-card__video"
                    src={videoUrl}
                    poster={thumbnailUrl || undefined}
                    muted={muted}
                    controls={false}
                    playsInline
                    preload="metadata"
                    draggable={false}
                    disablePictureInPicture
                    disableRemotePlayback
                    controlsList="nodownload noplaybackrate noremoteplayback"
                    onContextMenu={(event) => event.preventDefault()}
                    onDragStart={(event) => event.preventDefault()}
                    onCanPlay={() => setPlayerReady(true)}
                    onPlaying={() => setPlayingStarted(true)}
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={handleVideoEnded}
                    onError={handlePlayerError}
                  />
                </div>

                {!playingStarted && thumbnailUrl && (
                  <img
                    className="pop-thumbnail-card__poster"
                    src={thumbnailUrl}
                    alt={pop.title || "Pop"}
                    draggable={false}
                  />
                )}
              </>
            ) : thumbnailUrl ? (
              <img
                className="pop-thumbnail-card__poster"
                src={thumbnailUrl}
                alt={pop.title || "Pop"}
                draggable={false}
              />
            ) : (
              <div className="pop-thumbnail-card__empty">
                {playerError ? "Unable to play video" : "No media"}
              </div>
            )}

            <div className="pop-thumbnail-card__gradient pop-thumbnail-card__gradient--top" />
            <div className="pop-thumbnail-card__gradient pop-thumbnail-card__gradient--bottom" />

            {showHeartSplash && (
              <div className="pop-thumbnail-card__heart-splash">
                <Heart size={100} className="pop-thumbnail-card__splash-icon" />
              </div>
            )}

            {active && (
              <>
                {videoUrl && !playerError && (
                  <button
                    type="button"
                    className="pop-thumbnail-card__quick-mute"
                    onClick={(event) => {
                      event.stopPropagation();
                      onMuteChange?.(!muted);
                    }}
                    aria-label={muted ? "Unmute Pop" : "Mute Pop"}
                  >
                    {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                  </button>
                )}

                {userPaused && !playerError && playCount < 3 && (
                  <button
                    type="button"
                    className="pop-thumbnail-card__play-button"
                    onClick={handlePlayerClick}
                    aria-label="Play Pop"
                  >
                    <Play size={44} />
                  </button>
                )}

                {showActions && (
                  <div className="pop-thumbnail-card__right-actions">
                    <div className="pop-thumbnail-card__action-group">
                      <button
                        type="button"
                        className={`pop-thumbnail-card__action-btn ${
                          isSaved ? "pop-thumbnail-card__action-btn--saved" : ""
                        }`}
                        onClick={handleSave}
                        disabled={saving || !onSave}
                        aria-label={isSaved ? "Unsave Pop" : "Save Pop"}
                        aria-pressed={isSaved}
                      >
                        <Bookmark size={26} fill={isSaved ? "#fff" : "none"} />
                      </button>
                    </div>

                    {renderMenu()}

                    <div className="pop-thumbnail-card__disc-wrapper">
                      <div
                        className={`pop-thumbnail-card__disc ${
                          isPlaying ? "pop-thumbnail-card__disc--spinning" : ""
                        }`}
                      >
                        <Disc size={28} />

                        {pop.userAvatar && (
                          <img
                            src={pop.userAvatar}
                            alt="Audio avatar"
                            className="pop-thumbnail-card__disc-avatar"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="pop-thumbnail-card__bottom">
                  <div className="pop-thumbnail-card__content">
                    <div className="pop-thumbnail-card__info">
                      <div className="pop-thumbnail-card__author-header">
                        {pop.userAvatar && (
                          <img
                            src={pop.userAvatar}
                            alt={pop.username || "Creator"}
                            className="pop-thumbnail-card__avatar"
                          />
                        )}

                        <span className="pop-thumbnail-card__username">
                          @{pop.username || "user"}
                        </span>
                      </div>

                      {pop.title && (
                        <h2 className="pop-thumbnail-card__title">
                          {pop.title}
                        </h2>
                      )}

                      {pop.description && (
                        <div className="pop-thumbnail-card__caption-container">
                          <p
                            className={`pop-thumbnail-card__description ${
                              isExpanded
                                ? "pop-thumbnail-card__description--expanded"
                                : ""
                            }`}
                          >
                            {pop.description}
                          </p>

                          {pop.description.length > 60 && (
                            <button
                              type="button"
                              className="pop-thumbnail-card__more-btn"
                              onClick={(event) => {
                                event.stopPropagation();
                                setIsExpanded((previous) => !previous);
                              }}
                            >
                              {isExpanded ? "less" : "...more"}
                            </button>
                          )}
                        </div>
                      )}

                      <div className="pop-thumbnail-card__meta-bar">
                        <div className="pop-thumbnail-card__views">
                          <Eye size={12} />
                          <span>{formatMetric(pop.views)}</span>
                          <span>
                            {Number(pop.views) === 1 ? "view" : "views"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </article>
  );
};

export default PopThumbnailCard;
